<script setup lang="ts">
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { ref, onMounted, onUnmounted, watch, computed, createApp } from "vue";
import vuetify from "@/plugins/vuetify";
import FlightMapPopupCluster from "./FlightMapPopupCluster.vue";
import { availabilityColor } from "./availabilityColor";
import FlightDO = Modules.CampaignPlanner.Data.FlightDO;
import ScreenDO = Modules.CampaignPlanner.Data.ScreenDO;

const props = defineProps<{
	flight: FlightDO;
	screens?: ScreenDO[];
	excludedScreens?: ScreenDO[];
	locationPolygons?: object[] | null;
}>();

const mapContainer = ref<HTMLDivElement>();
const map = ref<mapboxgl.Map>();

const DUTCH_CENTER: mapboxgl.LngLatLike = { lng: 5.2913, lat: 52.1326 };
const DEFAULT_ZOOM = 7;

const arcPath = (cx: number, cy: number, r: number, pct: number): string => {
	if (pct <= 0) return "";
	if (pct >= 100) {
		// Full circle: two arcs since a single arc can't span 360°
		return `M ${cx} ${cy - r} A ${r} ${r} 0 1 1 ${(cx - 0.001).toFixed(3)} ${(
			cy - r
		).toFixed(3)}`;
	}
	const start = -Math.PI / 2;
	const end = start + (pct / 100) * 2 * Math.PI;
	const x1 = (cx + r * Math.cos(start)).toFixed(3);
	const y1 = (cy + r * Math.sin(start)).toFixed(3);
	const x2 = (cx + r * Math.cos(end)).toFixed(3);
	const y2 = (cy + r * Math.sin(end)).toFixed(3);
	return `M ${x1} ${y1} A ${r} ${r} 0 ${pct > 50 ? 1 : 0} 1 ${x2} ${y2}`;
};

const createProgressRingEl = (fillRate: number | null): HTMLElement => {
	const r = 10;
	const sw = 4;
	const size = (r + sw / 2 + 2) * 2;
	const cx = size / 2;
	const cy = size / 2;
	const pct = fillRate !== null ? Math.max(0, Math.min(100, Number(fillRate) || 0)) : 0;
	const color = availabilityColor(fillRate !== null ? 100 - pct : null);
	const arc = arcPath(cx, cy, r, pct);

	const el = document.createElement("div");
	el.style.cssText = `width:${size}px;height:${size}px;cursor:pointer;`;
	el.innerHTML = `
		<div style="opacity:0.8;width:100%;height:100%;">
			<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
				<circle cx="${cx}" cy="${cy}" r="${r}" fill="#fcfcfd"/>
				${
					fillRate === null
						? `<circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="${availabilityColor(null)}" stroke-width="2"/>`
						: fillRate === 0
						? `<circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="${color}" stroke-width="2"/>`
						: `<circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="rgba(0,0,0,0.08)" stroke-width="${sw}"/>
				<path d="${arc}" fill="none" stroke="${color}" stroke-width="${sw}" stroke-linecap="round"/>`
				}
			</svg>
		</div>`;
	return el;
};

const createClusterEl = (
	count: number,
	fillRateSum: number,
	fillRateCount: number
): HTMLElement => {
	const avgFillRate = fillRateCount > 0 ? fillRateSum / fillRateCount : null;
	const ringR = Math.round(
		count <= 25
			? 14 + (count / 25) * 10
			: 24 + Math.min((count - 25) / 75, 1) * 10
	);
	const sw = 4;
	const size = (ringR + sw / 2 + 2) * 2;
	const cx = size / 2;
	const cy = size / 2;
	const pct = avgFillRate !== null ? Math.max(0, Math.min(100, Number(avgFillRate) || 0)) : 0;
	const color = availabilityColor(avgFillRate !== null ? 100 - pct : null);
	const arc = arcPath(cx, cy, ringR, pct);
	const fontSize = ringR < 18 ? 11 : ringR < 24 ? 14 : 18;

	const el = document.createElement("div");
	el.style.cssText = `width:${size}px;height:${size}px;cursor:pointer;`;
	el.innerHTML = `
		<div style="opacity:0.8;width:100%;height:100%;">
			<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
				<circle cx="${cx}" cy="${cy}" r="${ringR}" fill="#fcfcfd"/>
				${
					avgFillRate === null
						? `<circle cx="${cx}" cy="${cy}" r="${ringR}" fill="none" stroke="${availabilityColor(null)}" stroke-width="2"/>`
						: avgFillRate === 0
						? `<circle cx="${cx}" cy="${cy}" r="${ringR}" fill="none" stroke="${color}" stroke-width="2"/>`
						: `<circle cx="${cx}" cy="${cy}" r="${ringR}" fill="none" stroke="rgba(0,0,0,0.08)" stroke-width="${sw}"/>
				<path d="${arc}" fill="none" stroke="${color}" stroke-width="${sw}" stroke-linecap="round"/>`
				}
				<text x="${cx}" y="${cy}" text-anchor="middle" dominant-baseline="central"
					font-family="'Open Sans Semibold', sans-serif" font-size="${fontSize}" fill="#000000">${count}</text>
			</svg>
		</div>`;
	return el;
};

const createExcludedRingEl = (): HTMLElement => {
	const r = 10;
	const sw = 2;
	const size = (r + sw / 2 + 2) * 2;
	const cx = size / 2;
	const cy = size / 2;

	const el = document.createElement("div");
	el.style.cssText = `width:${size}px;height:${size}px;cursor:default;`;
	el.innerHTML = `
		<div style="opacity:0.4;width:100%;height:100%;">
			<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
				<circle cx="${cx}" cy="${cy}" r="${r}" fill="#fcfcfd"/>
				<circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="#9ca3af" stroke-width="${sw}"/>
			</svg>
		</div>`;
	return el;
};

const createExcludedClusterEl = (count: number): HTMLElement => {
	const ringR = Math.round(
		count <= 25
			? 14 + (count / 25) * 10
			: 24 + Math.min((count - 25) / 75, 1) * 10
	);
	const sw = 2;
	const size = (ringR + sw / 2 + 2) * 2;
	const cx = size / 2;
	const cy = size / 2;
	const fontSize = ringR < 18 ? 11 : ringR < 24 ? 14 : 18;

	const el = document.createElement("div");
	el.style.cssText = `width:${size}px;height:${size}px;cursor:default;`;
	el.innerHTML = `
		<div style="opacity:0.4;width:100%;height:100%;">
			<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
				<circle cx="${cx}" cy="${cy}" r="${ringR}" fill="#fcfcfd"/>
				<circle cx="${cx}" cy="${cy}" r="${ringR}" fill="none" stroke="#9ca3af" stroke-width="${sw}"/>
				<text x="${cx}" y="${cy}" text-anchor="middle" dominant-baseline="central"
					font-family="'Open Sans Semibold', sans-serif" font-size="${fontSize}" fill="#9ca3af">${count}</text>
			</svg>
		</div>`;
	return el;
};

const locationPolygonsGeoJSON = computed<GeoJSON.GeoJSON>(() => ({
	type: "FeatureCollection",
	features: (props.locationPolygons ?? []).map((geom) => ({
		type: "Feature" as const,
		properties: {},
		geometry: geom as GeoJSON.Geometry,
	})),
}));

const updateLocationPolygons = () => {
	const source = map.value?.getSource("location-polygons") as
		| mapboxgl.GeoJSONSource
		| undefined;
	source?.setData(locationPolygonsGeoJSON.value);
};

const markers: mapboxgl.Marker[] = [];
const activePopup = ref<mapboxgl.Popup | null>(null);

const handleOutsideClick = (e: MouseEvent) => {
	if (
		activePopup.value &&
		mapContainer.value &&
		!mapContainer.value.contains(e.target as Node)
	) {
		activePopup.value.remove();
	}
};

const showPopup = (
	component: any,
	popupProps: any,
	coordinates: [number, number]
) => {
	activePopup.value?.remove();

	const div = document.createElement("div");
	let popup: mapboxgl.Popup;
	const app = createApp(component, {
		...popupProps,
		getPopup: () => popup,
	}).use(vuetify);
	app.mount(div);
	popup = new mapboxgl.Popup({
		closeButton: false,
		offset: 14,
		maxWidth: "none",
	})
		.setLngLat(coordinates)
		.setDOMContent(div)
		.addTo(map.value!);
	popup.on("close", () => {
		app.unmount();
		activePopup.value = null;
	});
	activePopup.value = popup;
};

const refreshMarkers = () => {
	markers.forEach((m) => m.remove());
	markers.length = 0;

	if (!map.value) return;

	// Excluded screens — rendered first so normal screens always appear on top
	const excludedIndividualFeatures = map.value.querySourceFeatures(
		"excluded-screens",
		{ filter: ["!", ["has", "point_count"]] }
	);

	const seenExcludedIds = new Set<number>();
	for (const feature of excludedIndividualFeatures) {
		const id = feature.properties?.id as number;
		if (seenExcludedIds.has(id)) continue;
		seenExcludedIds.add(id);

		const [lng, lat] = (feature.geometry as GeoJSON.Point).coordinates;
		const el = createExcludedRingEl();

		markers.push(
			new mapboxgl.Marker({ element: el, anchor: "center" })
				.setLngLat([lng, lat])
				.addTo(map.value!)
		);
	}

	// Excluded screens — clusters
	const excludedClusterFeatures = map.value.querySourceFeatures(
		"excluded-screens",
		{ filter: ["has", "point_count"] }
	);

	const seenExcludedClusters = new Set<number>();
	for (const feature of excludedClusterFeatures) {
		const clusterId = feature.properties?.cluster_id as number;
		if (seenExcludedClusters.has(clusterId)) continue;
		seenExcludedClusters.add(clusterId);

		const count = feature.properties?.point_count as number;
		const [lng, lat] = (feature.geometry as GeoJSON.Point).coordinates;
		const el = createExcludedClusterEl(count);

		markers.push(
			new mapboxgl.Marker({ element: el, anchor: "center" })
				.setLngLat([lng, lat])
				.addTo(map.value!)
		);
	}

	// Normal screens — rendered last so they always appear above excluded screens
	const individualFeatures = map.value.querySourceFeatures("screens", {
		filter: ["!", ["has", "point_count"]],
	});

	const seenIds = new Set<number>();
	for (const feature of individualFeatures) {
		const id = feature.properties?.id as number;
		if (seenIds.has(id)) continue;
		seenIds.add(id);

		const raw = feature.properties?.fillRate;
		const fillRate = raw != null && raw !== "null" ? Number(raw) : null;
		const [lng, lat] = (feature.geometry as GeoJSON.Point).coordinates;
		const screen = props.screens?.find((s) => s.id === id) ?? null;

		const el = createProgressRingEl(fillRate);
		el.addEventListener("click", (e) => {
			e.stopPropagation();
			showPopup(
				FlightMapPopupCluster,
				{
					count: 1,
					screens: screen ? [screen] : [],
					coordinates: [lng, lat] as [number, number],
				},
				[lng, lat]
			);
		});

		markers.push(
			new mapboxgl.Marker({ element: el, anchor: "center" })
				.setLngLat([lng, lat])
				.addTo(map.value!)
		);
	}

	const clusterFeatures = map.value.querySourceFeatures("screens", {
		filter: ["has", "point_count"],
	});

	const seenClusters = new Set<number>();
	for (const feature of clusterFeatures) {
		const clusterId = feature.properties?.cluster_id as number;
		if (seenClusters.has(clusterId)) continue;
		seenClusters.add(clusterId);

		const count = feature.properties?.point_count as number;
		const fillRateSum = Number(feature.properties?.fillRateSum) || 0;
		const fillRateCount = Number(feature.properties?.fillRateCount) || 0;
		const [lng, lat] = (feature.geometry as GeoJSON.Point).coordinates;

		const el = createClusterEl(count, fillRateSum, fillRateCount);
		el.addEventListener("click", (e) => {
			e.stopPropagation();
			showPopup(
				FlightMapPopupCluster,
				{
					clusterId,
					count,
					map: map.value,
					sourceId: "screens",
					coordinates: [lng, lat] as [number, number],
					screens: props.screens ?? [],
				},
				[lng, lat]
			);
		});

		markers.push(
			new mapboxgl.Marker({ element: el, anchor: "center" })
				.setLngLat([lng, lat])
				.addTo(map.value!)
		);
	}
};

const screensGeoJSON = computed<GeoJSON.GeoJSON>(() => ({
	type: "FeatureCollection",
	features: (props.screens ?? [])
		.filter((s) => s.address_data?.lat && s.address_data?.long)
		.map((s) => ({
			type: "Feature" as const,
			properties: {
				id: s.id,
				name: s.name ?? "",
				fillRate: s.availability?.fill_rate != null ? s.availability.fill_rate * 100 : null,
			},
			geometry: {
				type: "Point" as const,
				coordinates: [s.address_data!.long!, s.address_data!.lat!],
			},
		})),
}));

const excludedScreensGeoJSON = computed<GeoJSON.GeoJSON>(() => ({
	type: "FeatureCollection",
	features: (props.excludedScreens ?? [])
		.filter((s) => s.address_data?.lat && s.address_data?.long)
		.map((s) => ({
			type: "Feature" as const,
			properties: { id: s.id, name: s.name ?? "" },
			geometry: {
				type: "Point" as const,
				coordinates: [s.address_data!.long!, s.address_data!.lat!],
			},
		})),
}));

const resizeObserver = new ResizeObserver(() => map.value?.resize());

onMounted(() => {
	mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN ?? "";

	map.value = new mapboxgl.Map({
		container: mapContainer.value!,
		style: "mapbox://styles/mapbox/light-v11",
		center: DUTCH_CENTER,
		zoom: DEFAULT_ZOOM,
		fadeDuration: 0,
	});

	resizeObserver.observe(mapContainer.value!);
	document.addEventListener("click", handleOutsideClick);

	map.value.on("load", () => {
		map.value!.addSource("location-polygons", {
			type: "geojson",
			data: locationPolygonsGeoJSON.value,
		});
		map.value!.addLayer({
			id: "location-fill",
			type: "fill",
			source: "location-polygons",
			paint: { "fill-color": "#2563eb", "fill-opacity": 0.07 },
		});
		map.value!.addLayer({
			id: "location-outline",
			type: "line",
			source: "location-polygons",
			paint: { "line-color": "#2563eb", "line-width": 2 },
		});

		map.value!.addSource("screens", {
			type: "geojson",
			data: screensGeoJSON.value,
			cluster: true,
			clusterRadius: 50,
			clusterMaxZoom: 14,
			clusterProperties: {
				fillRateSum: ["+", ["coalesce", ["get", "fillRate"], 0]],
				fillRateCount: ["+", ["case", ["!=", ["get", "fillRate"], null], 1, 0]],
			},
		});

		// Hidden layer keeps the source tile cache active so querySourceFeatures returns results
		map.value!.addLayer({
			id: "screens-tile-loader",
			type: "circle",
			source: "screens",
			paint: { "circle-radius": 0, "circle-opacity": 0 },
		});

		map.value!.addSource("excluded-screens", {
			type: "geojson",
			data: excludedScreensGeoJSON.value,
			cluster: true,
			clusterRadius: 50,
			clusterMaxZoom: 14,
		});

		map.value!.addLayer({
			id: "excluded-screens-tile-loader",
			type: "circle",
			source: "excluded-screens",
			paint: { "circle-radius": 0, "circle-opacity": 0 },
		});

		map.value!.on("moveend", refreshMarkers);
		map.value!.once("idle", refreshMarkers);
	});
});

watch(screensGeoJSON, (geoJSON) => {
	const source = map.value?.getSource("screens") as
		| mapboxgl.GeoJSONSource
		| undefined;
	source?.setData(geoJSON);
	map.value?.once("idle", refreshMarkers);
});

watch(excludedScreensGeoJSON, (geoJSON) => {
	const source = map.value?.getSource("excluded-screens") as
		| mapboxgl.GeoJSONSource
		| undefined;
	source?.setData(geoJSON);
	map.value?.once("idle", refreshMarkers);
});

watch(locationPolygonsGeoJSON, updateLocationPolygons);

onUnmounted(() => {
	markers.forEach((m) => m.remove());
	resizeObserver.disconnect();
	document.removeEventListener("click", handleOutsideClick);
	map.value?.remove();
});
</script>

<template>
	<div ref="mapContainer" class="map-container">
		<div class="map-legend-label">
			<span class="map-legend-label__key">Legend:</span> Fill Rate
		</div>
	</div>
</template>

<style>
.mapboxgl-popup-content {
	background-color: #334155;
	border-radius: 10px;
	padding: 12px;
	box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
}
.mapboxgl-popup-anchor-top .mapboxgl-popup-tip {
	border-bottom-color: #334155;
}
.mapboxgl-popup-anchor-bottom .mapboxgl-popup-tip {
	border-top-color: #334155;
}
.mapboxgl-popup-anchor-left .mapboxgl-popup-tip {
	border-right-color: #334155;
}
.mapboxgl-popup-anchor-right .mapboxgl-popup-tip {
	border-left-color: #334155;
}
</style>

<style scoped lang="scss">
.map-container {
	width: 100%;
	height: 100%;
	position: relative;
}

.map-legend-label {
	position: absolute;
	bottom: 10px;
	left: 10px;
	background: #fff;
	border-radius: 4px;
	box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
	padding: 5px 10px;
	font-family: "Open Sans", sans-serif;
	font-size: 11px;
	color: #404040;
	pointer-events: none;

	&__key {
		font-weight: 700;
		color: #404040;
	}
}

:deep(.mapboxgl-ctrl) {
	display: none !important;
}
</style>
